import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  ReloadOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getAiGuidePolicy,
  getMembershipPlanPolicy,
  getPriceMonitorPolicy,
  getRebatePolicy,
  updateAiGuidePolicy,
  updateMembershipPlanPolicy,
  updatePriceMonitorPolicy,
  updateRebatePolicy,
} from '../../api/memberships';
import { getInviteBenefitPolicy, updateInviteBenefitPolicy } from '../../api/invites';
import {
  getTaobaoCapabilities,
  getWithdrawalPolicy,
  updateTaobaoCapability,
  updateWithdrawalPolicy,
  getAiGuideVisibility,
  updateAiGuideVisibility,
} from '../../api/systemConfig';
import { distributionChannelLabels } from '../../content/distributionChannels';
import { priceMonitorPolicyValidationError } from '../Memberships/policyForm';
import {
  memberCapValidationError,
  rateDurationValidationError,
  toInvitePolicyFormValues,
  toInvitePolicyPayload,
} from '../Invites/policyForm';
import './index.css';

const sectionLoaders = {
  membership: () => Promise.allSettled([getMembershipPlanPolicy(), getRebatePolicy()]),
  growth: () => Promise.allSettled([
    getAiGuidePolicy(), getPriceMonitorPolicy(), getInviteBenefitPolicy(), getAiGuideVisibility(),
  ]),
  taobao: () => getTaobaoCapabilities(),
  withdrawal: () => getWithdrawalPolicy(),
};

const updatedLabel = (value) => (
  value ? `最近更新 ${dayjs(value).format('YYYY-MM-DD HH:mm')}` : '使用系统默认配置'
);

const SystemConfig = () => {
  const [planForm] = Form.useForm();
  const [rebateForm] = Form.useForm();
  const [aiForm] = Form.useForm();
  const [aiVisibilityForm] = Form.useForm();
  const [priceMonitorForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [withdrawalForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [updatedAt, setUpdatedAt] = useState({});
  const [capabilities, setCapabilities] = useState([]);
  const [aiVisibility, setAiVisibility] = useState(null);
  const minimumEnabled = Form.useWatch('minimumEnabled', withdrawalForm);
  const singleMaximumEnabled = Form.useWatch('singleMaximumEnabled', withdrawalForm);
  const dailyMaximumEnabled = Form.useWatch('dailyMaximumEnabled', withdrawalForm);
  const aiGloballyEnabled = Form.useWatch('globallyEnabled', aiVisibilityForm);

  const applyMembership = useCallback((results) => {
    const [planResult, rebateResult] = results;
    const errors = [];
    if (planResult.status === 'fulfilled') {
      const policy = planResult.value;
      planForm.setFieldsValue({
        monthlyPrice: Number(policy.monthlyPrice),
        yearlyPrice: Number(policy.yearlyPrice),
      });
      setUpdatedAt((current) => ({ ...current, plan: policy.updatedAt }));
    } else errors.push('会员套餐');
    if (rebateResult.status === 'fulfilled') {
      const policy = rebateResult.value;
      rebateForm.setFieldsValue({
        freeUserRatePercent: Number(policy.freeUserRate) * 100,
        paidMemberRatePercent: Number(policy.paidMemberRate) * 100,
      });
      setUpdatedAt((current) => ({ ...current, rebate: policy.updatedAt }));
    } else errors.push('返利比例');
    return errors.length ? `${errors.join('、')}加载失败` : '';
  }, [planForm, rebateForm]);

  const applyGrowth = useCallback((results) => {
    const [aiResult, monitorResult, inviteResult, visibilityResult] = results;
    const errors = [];
    if (aiResult.status === 'fulfilled') {
      aiForm.setFieldsValue({ freeWeeklyLimit: Number(aiResult.value.freeWeeklyLimit) });
      setUpdatedAt((current) => ({ ...current, ai: aiResult.value.updatedAt }));
    } else errors.push('AI 导购');
    if (monitorResult.status === 'fulfilled') {
      const policy = monitorResult.value;
      priceMonitorForm.setFieldsValue({
        freeActiveMonitorLimit: Number(policy.freeActiveMonitorLimit),
        freeHistoryDays: Number(policy.freeHistoryDays),
        paidActiveMonitorLimit: Number(policy.paidActiveMonitorLimit),
        paidHistoryDays: Number(policy.paidHistoryDays),
      });
      setUpdatedAt((current) => ({ ...current, monitor: policy.updatedAt }));
    } else errors.push('价格监控');
    if (inviteResult.status === 'fulfilled') {
      inviteForm.setFieldsValue(toInvitePolicyFormValues(inviteResult.value));
      setUpdatedAt((current) => ({ ...current, invite: inviteResult.value.updatedAt }));
    } else errors.push('邀请权益');
    if (visibilityResult.status === 'fulfilled') {
      const visibility = visibilityResult.value;
      setAiVisibility(visibility);
      aiVisibilityForm.setFieldsValue({
        globallyEnabled: Boolean(visibility.globallyEnabled),
        disabledChannels: visibility.disabledChannels || [],
      });
      setUpdatedAt((current) => ({ ...current, aiVisibility: visibility.updatedAt }));
    } else errors.push('AI 入口开关');
    return errors.length ? `${errors.join('、')}加载失败` : '';
  }, [aiForm, aiVisibilityForm, inviteForm, priceMonitorForm]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      sectionLoaders.membership(),
      sectionLoaders.growth(),
      sectionLoaders.taobao(),
      sectionLoaders.withdrawal(),
    ]);
    const errors = {};
    if (results[0].status === 'fulfilled') errors.membership = applyMembership(results[0].value);
    else errors.membership = '会员与返利配置加载失败';
    if (results[1].status === 'fulfilled') errors.growth = applyGrowth(results[1].value);
    else errors.growth = '增长权益配置加载失败';
    if (results[2].status === 'fulfilled') setCapabilities(results[2].value || []);
    else errors.taobao = '淘宝接口配置加载失败';
    if (results[3].status === 'fulfilled') {
      const policy = results[3].value;
      withdrawalForm.setFieldsValue({
        minimumEnabled: Boolean(policy.minimumEnabled),
        minimumAmount: Number(policy.minimumAmount),
        singleMaximumEnabled: Boolean(policy.singleMaximumEnabled),
        singleMaximumAmount: Number(policy.singleMaximumAmount),
        dailyMaximumEnabled: Boolean(policy.dailyMaximumEnabled),
        dailyMaximumAmount: Number(policy.dailyMaximumAmount),
      });
      setUpdatedAt((current) => ({ ...current, withdrawal: policy.updatedAt }));
    } else errors.withdrawal = '提现规则加载失败';
    setSectionErrors(errors);
    setLoading(false);
  }, [applyGrowth, applyMembership, withdrawalForm]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const retrySection = async (section) => {
    setSectionErrors((current) => ({ ...current, [section]: '' }));
    try {
      const result = await sectionLoaders[section]();
      if (section === 'membership') setSectionErrors((current) => ({ ...current, membership: applyMembership(result) }));
      if (section === 'growth') setSectionErrors((current) => ({ ...current, growth: applyGrowth(result) }));
      if (section === 'taobao') setCapabilities(result || []);
      if (section === 'withdrawal') {
        withdrawalForm.setFieldsValue(result);
        setUpdatedAt((current) => ({ ...current, withdrawal: result.updatedAt }));
      }
    } catch (error) {
      setSectionErrors((current) => ({ ...current, [section]: error?.message || '加载失败' }));
    }
  };

  const save = async (key, action, successText, onConflict) => {
    setSaving((current) => ({ ...current, [key]: true }));
    try {
      const policy = await action();
      setUpdatedAt((current) => ({ ...current, [key]: policy.updatedAt }));
      message.success(successText);
      return policy;
    } catch (error) {
      if (error?.status === 409 || error?.code === 409) {
        message.info('配置版本已变化，请刷新后重试');
        if (onConflict) await onConflict();
      }
      return null;
    } finally {
      setSaving((current) => ({ ...current, [key]: false }));
    }
  };

  const savePlan = async () => {
    const values = await planForm.validateFields();
    await save('plan', () => updateMembershipPlanPolicy(values), '会员套餐价格已更新');
  };

  const saveRebate = async () => {
    const values = await rebateForm.validateFields();
    await save('rebate', () => updateRebatePolicy({
      freeUserRate: values.freeUserRatePercent / 100,
      paidMemberRate: values.paidMemberRatePercent / 100,
    }), '返利比例已更新');
  };

  const saveAi = async () => {
    const values = await aiForm.validateFields();
    await save('ai', () => updateAiGuidePolicy(values), 'AI 导购权益已更新');
  };

  const saveAiVisibility = async () => {
    const values = await aiVisibilityForm.validateFields();
    const updated = await save('aiVisibility', () => updateAiGuideVisibility({
      globallyEnabled: values.globallyEnabled,
      disabledChannels: values.disabledChannels || [],
      version: aiVisibility.version,
    }), 'AI 导购入口配置已更新', () => retrySection('growth'));
    if (updated) {
      setAiVisibility(updated);
      aiVisibilityForm.setFieldsValue(updated);
    }
  };

  const saveMonitor = async () => {
    const values = await priceMonitorForm.validateFields();
    const validationError = priceMonitorPolicyValidationError(values);
    if (validationError) return message.error(validationError);
    await save('monitor', () => updatePriceMonitorPolicy(values), '价格监控权益已更新');
  };

  const saveInvite = async () => {
    const values = await inviteForm.validateFields();
    await save('invite', () => updateInviteBenefitPolicy(toInvitePolicyPayload(values)), '邀请权益已更新');
  };

  const saveWithdrawal = async () => {
    const values = await withdrawalForm.validateFields();
    await save(
      'withdrawal',
      () => updateWithdrawalPolicy(values),
      '提现金额规则已更新',
    );
  };

  const changeCapability = (capability, enabled) => {
    Modal.confirm({
      title: enabled ? `开启${capability.displayName}` : `关闭${capability.displayName}`,
      content: enabled
        ? '开启前将向淘宝发送一次受控权限检测请求。检测失败时不会启用。'
        : '关闭立即生效，后续请求不会再调用对应的淘宝权限型 API。',
      okText: enabled ? '检测并开启' : '确认关闭',
      cancelText: '取消',
      onOk: async () => {
        setSaving((current) => ({ ...current, [capability.capability]: true }));
        try {
          const next = await updateTaobaoCapability(capability.capability, {
            enabled,
            version: capability.version,
          });
          setCapabilities((current) => current.map((item) => (
            item.capability === next.capability ? next : item
          )));
          message.success(enabled ? '权限检测通过，接口已开启' : '接口已关闭');
        } catch (error) {
          if (error?.status === 409) await retrySection('taobao');
        } finally {
          setSaving((current) => ({ ...current, [capability.capability]: false }));
        }
      },
    });
  };

  const errorAlert = (section) => sectionErrors[section] ? (
    <Alert
      showIcon
      type="error"
      message={sectionErrors[section]}
      action={<Button size="small" onClick={() => retrySection(section)}>重试</Button>}
      style={{ marginBottom: 18 }}
    />
  ) : null;

  return (
    <div className="system-config-page">
      <header className="system-config-header">
        <div>
          <div className="system-config-title-row">
            <SettingOutlined style={{ color: '#ff2442', fontSize: 22 }} />
            <Typography.Title level={2} style={{ margin: 0, fontSize: 22 }}>系统配置</Typography.Title>
          </div>
          <Typography.Text type="secondary">集中管理可在线生效的业务策略，敏感凭证和部署参数不在此处开放。</Typography.Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>刷新配置</Button>
      </header>

      <div className="system-config-layout">
        <nav className="system-config-nav" aria-label="配置分区">
          <a href="#membership">会员与返利</a>
          <a href="#growth">增长权益</a>
          <a href="#withdrawal">提现规则</a>
          <a href="#taobao">淘宝接口</a>
          <a href="#control-centers">专业控制中心</a>
        </nav>

        <main className="system-config-content">
          {loading ? <div style={{ padding: 72, textAlign: 'center' }}><Spin size="large" /></div> : null}
          <section id="membership" className="config-section">
            <div className="config-section-heading">
              <div><h3>会员与返利</h3><p>会员售价与用户返利比例，修改后立即用于新订单和新购买。</p></div>
            </div>
            {errorAlert('membership')}
            <div className="config-subsection">
              <div className="config-subsection-title"><span>套餐价格</span><Typography.Text type="secondary">{updatedLabel(updatedAt.plan)}</Typography.Text></div>
              <Form form={planForm} layout="vertical">
                <div className="config-form-grid">
                  <Form.Item name="monthlyPrice" label="月度会员" rules={[{ required: true, message: '请输入月度会员价格' }]}><InputNumber min={0.01} precision={2} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
                  <Form.Item name="yearlyPrice" label="年度会员" dependencies={['monthlyPrice']} rules={[{ required: true, message: '请输入年度会员价格' }, ({ getFieldValue }) => ({ validator(_, value) { const monthlyPrice = getFieldValue('monthlyPrice'); return value == null || monthlyPrice == null || value < monthlyPrice * 12 ? Promise.resolve() : Promise.reject(new Error('年价必须低于12个月月费总和')); } })]}><InputNumber min={0.01} precision={2} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
                </div>
                <div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.plan} onClick={savePlan}>保存套餐价格</Button></div>
              </Form>
            </div>
            <div className="config-subsection">
              <div className="config-subsection-title"><span>返利比例</span><Typography.Text type="secondary">{updatedLabel(updatedAt.rebate)}</Typography.Text></div>
              <Form form={rebateForm} layout="vertical">
                <div className="config-form-grid">
                  <Form.Item name="freeUserRatePercent" label="普通用户" rules={[{ required: true, message: '请输入普通用户比例' }]}><InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: '100%' }} /></Form.Item>
                  <Form.Item name="paidMemberRatePercent" label="付费会员" dependencies={['freeUserRatePercent']} rules={[{ required: true, message: '请输入会员比例' }, ({ getFieldValue }) => ({ validator(_, value) { const freeRate = getFieldValue('freeUserRatePercent'); return value == null || freeRate == null || value >= freeRate ? Promise.resolve() : Promise.reject(new Error('会员比例不能低于普通用户')); } })]}><InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: '100%' }} /></Form.Item>
                </div>
                <div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.rebate} onClick={saveRebate}>保存返利比例</Button></div>
              </Form>
            </div>
          </section>

          <section id="growth" className="config-section">
            <div className="config-section-heading"><div><h3>增长权益</h3><p>控制 AI 导购、价格监控和邀请奖励的用户权益边界。</p></div></div>
            {errorAlert('growth')}
            <div className="config-subsection">
              <div className="config-subsection-title"><span>AI 导购入口</span><Typography.Text type="secondary">{updatedLabel(updatedAt.aiVisibility)}</Typography.Text></div>
              <Form form={aiVisibilityForm} layout="vertical">
                <div className="config-form-grid">
                  <Form.Item name="globallyEnabled" label="全量展示" valuePropName="checked">
                    <Switch aria-label="全量展示 AI 导购入口" checkedChildren="开启" unCheckedChildren="关闭" />
                  </Form.Item>
                  <Form.Item name="disabledChannels" label="按渠道隐藏">
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="选择需要隐藏 AI 导购的渠道"
                      options={(aiVisibility?.supportedChannels || []).map((channel) => ({
                        value: channel,
                        label: distributionChannelLabels[channel] || channel,
                      }))}
                    />
                  </Form.Item>
                </div>
                <Alert
                  showIcon
                  type={aiGloballyEnabled ? 'info' : 'warning'}
                  message={aiGloballyEnabled
                    ? '全局开启时，仅所选渠道隐藏 AI 导购入口。'
                    : '全局关闭优先，所有渠道的 AI 导购入口和服务端调用都会停用。'}
                  style={{ marginBottom: 14 }}
                />
                <div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.aiVisibility} disabled={!aiVisibility} onClick={saveAiVisibility}>保存 AI 入口配置</Button></div>
              </Form>
            </div>
            <div className="config-subsection">
              <div className="config-subsection-title"><span>AI 导购</span><Typography.Text type="secondary">{updatedLabel(updatedAt.ai)}</Typography.Text></div>
              <Form form={aiForm} layout="vertical"><div className="config-form-grid"><Form.Item name="freeWeeklyLimit" label="普通用户每周次数" rules={[{ required: true }]}><InputNumber min={0} max={1000} precision={0} addonAfter="次" style={{ width: '100%' }} /></Form.Item></div><div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.ai} onClick={saveAi}>保存 AI 权益</Button></div></Form>
            </div>
            <div className="config-subsection">
              <div className="config-subsection-title"><span>价格监控</span><Typography.Text type="secondary">{updatedLabel(updatedAt.monitor)}</Typography.Text></div>
              <Form form={priceMonitorForm} layout="vertical"><div className="config-form-grid">
                <Form.Item name="freeActiveMonitorLimit" label="普通用户监控数量" rules={[{ required: true }]}><InputNumber min={1} max={1000} precision={0} addonAfter="项" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="freeHistoryDays" label="普通用户历史天数" rules={[{ required: true }]}><InputNumber min={1} max={365} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="paidActiveMonitorLimit" label="会员监控数量" rules={[{ required: true }]}><InputNumber min={1} max={1000} precision={0} addonAfter="项" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="paidHistoryDays" label="会员历史天数" rules={[{ required: true }]}><InputNumber min={1} max={365} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
              </div><div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.monitor} onClick={saveMonitor}>保存监控权益</Button></div></Form>
            </div>
            <div className="config-subsection">
              <div className="config-subsection-title"><span>邀请权益</span><Typography.Text type="secondary">{updatedLabel(updatedAt.invite)}</Typography.Text></div>
              <Form form={inviteForm} layout="vertical"><div className="config-form-grid">
                <Form.Item name="newUserBenefitDays" label="新用户基础返利期" rules={[{ required: true }]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="inviteeExtraBenefitDays" label="填写邀请码额外天数" rules={[{ required: true }]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="firstOrderMinAmount" label="合格首单最低实付" rules={[{ required: true }]}><InputNumber min={0.01} max={100000} precision={2} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="ratePerInvitePercent" label="每个合格邀请提升" rules={[{ required: true }]}><InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="rateBenefitDays" label="比例奖励有效期" rules={[{ required: true }, ({ getFieldValue }) => ({ validator(_, value) { const error = rateDurationValidationError(getFieldValue('ratePerInvitePercent'), value); return error ? Promise.reject(new Error(error)) : Promise.resolve(); } })]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="capExtendDays" label="达到100%后每人延长" rules={[{ required: true }]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="memberExtendDays" label="会员每个邀请延长" rules={[{ required: true }]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="memberMaxExtendDaysPerMonth" label="会员每月最多延长" rules={[{ required: true }, ({ getFieldValue }) => ({ validator(_, value) { const error = memberCapValidationError(getFieldValue('memberExtendDays'), value); return error ? Promise.reject(new Error(error)) : Promise.resolve(); } })]}><InputNumber min={0} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} /></Form.Item>
              </div><div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.invite} onClick={saveInvite}>保存邀请权益</Button></div></Form>
            </div>
          </section>

          <section id="withdrawal" className="config-section">
            <div className="config-section-heading">
              <div><h3>提现规则</h3><p>金额限制可独立启停；全部关闭时，用户可从 0.01 元起提现，最高不超过可提现余额。</p></div>
              <Typography.Text type="secondary">{updatedLabel(updatedAt.withdrawal)}</Typography.Text>
            </div>
            {errorAlert('withdrawal')}
            <Form form={withdrawalForm} layout="vertical">
              <div className="withdrawal-policy-row">
                <Form.Item name="minimumEnabled" label="最低单笔" valuePropName="checked"><Switch aria-label="启用最低单笔提现限制" /></Form.Item>
                <Form.Item name="minimumAmount" label="最低金额" rules={[{ required: true, message: '请输入最低金额' }]}><InputNumber min={0.01} max={9999999999.99} precision={2} disabled={!minimumEnabled} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
              </div>
              <div className="withdrawal-policy-row">
                <Form.Item name="singleMaximumEnabled" label="最高单笔" valuePropName="checked"><Switch aria-label="启用最高单笔提现限制" /></Form.Item>
                <Form.Item name="singleMaximumAmount" label="最高金额" dependencies={['minimumEnabled', 'minimumAmount']} rules={[{ required: true, message: '请输入最高金额' }, ({ getFieldValue }) => ({ validator(_, value) { const minEnabled = getFieldValue('minimumEnabled'); const min = getFieldValue('minimumAmount'); return !singleMaximumEnabled || !minEnabled || value == null || min == null || value >= min ? Promise.resolve() : Promise.reject(new Error('最高单笔不能低于最低单笔')); } })]}><InputNumber min={0.01} max={9999999999.99} precision={2} disabled={!singleMaximumEnabled} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
              </div>
              <div className="withdrawal-policy-row">
                <Form.Item name="dailyMaximumEnabled" label="每日累计" valuePropName="checked"><Switch aria-label="启用每日累计提现上限" /></Form.Item>
                <Form.Item name="dailyMaximumAmount" label="每日上限" dependencies={['minimumEnabled', 'minimumAmount']} rules={[{ required: true, message: '请输入每日上限' }, ({ getFieldValue }) => ({ validator(_, value) { const minEnabled = getFieldValue('minimumEnabled'); const min = getFieldValue('minimumAmount'); return !dailyMaximumEnabled || !minEnabled || value == null || min == null || value >= min ? Promise.resolve() : Promise.reject(new Error('每日上限不能低于最低单笔')); } })]}><InputNumber min={0.01} max={9999999999.99} precision={2} disabled={!dailyMaximumEnabled} addonBefore="¥" style={{ width: '100%' }} /></Form.Item>
              </div>
              <div className="config-form-actions"><Button type="primary" icon={<SaveOutlined />} loading={saving.withdrawal} onClick={saveWithdrawal}>保存提现规则</Button></div>
            </Form>
          </section>

          <section id="taobao" className="config-section">
            <div className="config-section-heading"><div><h3>淘宝接口</h3><p>仅控制依赖额外权限包的接口。缓存 CPS 链接和用户 relation_id 归因不受转链开关影响。</p></div><Tag>默认关闭</Tag></div>
            {errorAlert('taobao')}
            {capabilities.map((capability) => (
              <div className="capability-row" key={capability.capability}>
                <div>
                  <div className="capability-title"><span>{capability.displayName}</span><Tag color={capability.effectiveEnabled ? 'success' : 'default'}>{capability.effectiveEnabled ? '已生效' : '已关闭'}</Tag>{capability.permissionStatus === 'AVAILABLE' ? <Tag color="processing">权限可用</Tag> : null}</div>
                  <div className="capability-meta">
                    {capability.forceDisabled ? '部署环境已强制关闭，后台开关暂不生效。' : '开启时会先执行一次受控权限检测。'}
                    {capability.permissionCheckedAt ? ` 最近检测 ${dayjs(capability.permissionCheckedAt).format('YYYY-MM-DD HH:mm')}` : ''}
                  </div>
                </div>
                <Switch
                  aria-label={`切换${capability.displayName}`}
                  checked={capability.requestedEnabled}
                  loading={saving[capability.capability]}
                  disabled={capability.forceDisabled}
                  onChange={(checked) => changeCapability(capability, checked)}
                />
              </div>
            ))}
          </section>

          <section id="control-centers" className="config-section">
            <div className="config-section-heading"><div><h3>专业控制中心</h3><p>高风险或生命周期配置保留在专用页面，避免通用配置误操作。</p></div></div>
            <div className="control-center-links">
              <Link className="control-center-link" to="/admin/risk-control"><span><strong>风控中心</strong><br /><Typography.Text type="secondary">规则版本与风险事件</Typography.Text></span><ArrowRightOutlined /></Link>
              <Link className="control-center-link" to="/admin/taobao-integration"><span><strong>淘宝授权</strong><br /><Typography.Text type="secondary">OAuth 与渠道能力状态</Typography.Text></span><ArrowRightOutlined /></Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default SystemConfig;
